// poreReductionsData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[] | any;
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const poreReductions: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Best Pore Reduction Treatment in Dubai | Minimize Large Pores",
            "description": "Effective pore reduction treatments in Dubai to minimize large pores and improve skin texture. Get smoother, clearer skin with our specialized procedures. Book your consultation today.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/pore-reduction/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Best Pore Reduction Treatment in Dubai | Minimize Large Pores",
                "description": "Effective pore reduction treatments in Dubai to minimize large pores and improve skin texture. Get smoother, clearer skin with our specialized procedures. Book your consultation today.",
                "url": "https://elitebodyhome.com/services/pore-reduction/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Pore Reduction Treatment Dubai",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Best Pore Reduction Treatment in Dubai | Minimize Large Pores",
                "description": "Effective pore reduction treatments in Dubai to minimize large pores and improve skin texture. Get smoother, clearer skin with our specialized procedures. Book your consultation today.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Pore Reduction"],
        "hero": {
            "title": "Pore Reduction",
            "subtitle": "",
            "description": "Large pores is one of the most common skin concerns, the skin looks uneven and is susceptible for blackheads, whiteheads and acne. Thankfully, pore-shrinking treatments provide a safe and effective means to reduce pore size and improve overall skin texture."
        },
        "howItWorks": {
            "title": "Pore Reduction Procedure",
            "description": "Pore-tightening treatments are targeted at reducing the look of large pores for clearer skin that is smoother and finer."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Microneedling",
                    "desc": "Promotes collagen production, firming and shrinking pores over time.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Chemical Peels",
                    "desc": "Helps to minimize the look of pores by deep cleansing and exfoliating to clear pores of excess oil.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "Laser Therapy",
                    "desc": "Targets deeper skin layers, promoting collagen production and reducing pore size for a flawless appearance.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "PRP Treatment",
                    "desc": "Uses your own blood growth factors to stimulate skin repair and collagen production to minimize pore size.",
                    "icon": "fa-solid fa-dna"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Pore Reduction",
            "subheading": "Why effective pore reduction is beneficial",
            "para": "After pore reduction treatment, not only the size of pores, but also the health of the skin and how it looks can be improved. Here's what these treatments can do for your skincare routine:",
            "items": [
                {
                    "title": "Smoother skin texture",
                    "desc": "Pore minimizing treatments smooth out the surface of your skin, so that it looks more even.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Reduced acne and blackheads",
                    "desc": "Pore size is reduced with these treatments in order to stop clogging of the pores, which zits rely on in order to pop.",
                    "icon": "fa-solid fa-bacteria"
                },
                {
                    "title": "Younger-looking skin",
                    "desc": "Reducing pore appearance can lead to brighter, younger-looking skin.",
                    "icon": "fa-solid fa-child"
                },
                {
                    "title": "Boost in confidence",
                    "desc": "Clearer and more refined skin leads to better self-esteem and confidence.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Pore reduction treatments are generally good for the typical patient who has problems with enlarged pores, but some types of treatments are better than others based on some conditions and skin types. Here is who might be a good candidate:",
            "items": [
                {
                    "title": "Oily skin types",
                    "desc": "If you have oily skin, having too much oil production can make your pores look larger, so treatments are especially beneficial.",
                    "icon": "fa-solid fa-oil-can"
                },
                {
                    "title": "Mature skin",
                    "desc": "As the skin ages, collagen production slows so pores may appear more noticeable. Collagen-enhancing treatments work well for mature skin.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "Acne-prone skin",
                    "desc": "People who have acne-prone and oily skin tend to benefit from treatments that unclog and reduce pore size.",
                    "icon": "fa-solid fa-bacteria"
                },
                {
                    "title": "Individuals with sun damage",
                    "desc": "Sun exposure can cause pores to stretch, so laser resurfacing targets pore size and sun damage together.",
                    "icon": "fa-solid fa-sun"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation Before Treatment",
            "subheading": "",
            "para": "There are some things you should do if you want to get ready for a pore reduction treatment, this is important if you want the best possible results and to minimize the chances of side effects. Here's how to prepare:",
            "items": [
                {
                    "title": "Avoid sun exposure",
                    "desc": "Sunburns or tan skin can interfere with treatments, particularly laser resurfacing. Avoid sun exposure and use a high-SPF sunscreen three weeks before your treatment.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "Stop using abrasive skin care products",
                    "desc": "Retinoids, acids or exfoliants can cause the skin to become irritated or to peel before some treatments. Your dermatologist may have you stop these a few days before.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "Stay hydrated",
                    "desc": "Skin that's moistened recovers better, responds better to treatments and heals quicker. Drink high volumes of water a few days before your appointment.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Consult your dermatologist",
                    "desc": "Book a consultation to talk about possible skin conditions or sensitivities in advance, making sure that the treatment can be tailor-made according to your skin's needs.",
                    "icon": "fa-solid fa-user-doctor"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "What to expect after pore reduction procedures:",
            "items": [
                {
                    "title": "More even and smoother skin texture",
                    "desc": "Treatments will minimize the appearance of pores and improve overall skin texture.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Less oily, fewer breakouts",
                    "desc": "With smaller, less congested pores, you'll see fewer breakouts and shiny skin.",
                    "icon": "fa-solid fa-bacteria"
                },
                {
                    "title": "Healthier, younger-looking skin",
                    "desc": "By targeting pore size/texture, these treatments also help improve the brightness/younger appearance of skin.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long does it take to see results from pore reduction treatments?",
                    "answer": "Results can vary depending on the type of treatment. Laser resurfacing and microneedling typically show improvement within a few weeks, while chemical peels offer more immediate results. Full benefits usually appear after a series of treatments."
                },
                {
                    "question": "Are pore reduction treatments painful?",
                    "answer": "Most pore reduction treatments are minimally invasive and cause little to no discomfort. Laser treatments and microneedling may cause slight tingling or discomfort, but numbing creams are often applied beforehand to reduce any pain."
                },
                {
                    "question": "Can pore reduction treatments be combined with other skincare procedures?",
                    "answer": "Yes, many pore reduction treatments can be combined with other cosmetic procedures, such as facials, dermaplaning, or even anti-aging treatments like Botox or fillers, to enhance overall skin texture and appearance."
                },
                {
                    "question": "Is there any downtime after treatment?",
                    "answer": "Most treatments have minimal downtime. Laser resurfacing and deeper chemical peels may require a few days of recovery, while others, like HydraFacials and microneedling, allow you to return to daily activities almost immediately."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Bandfsner-Image-scaled.jpg",
            "beforeAfter": [
                { "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/sfsfseg-1.jpg", "alt": "pore reduction treatment" },
                { "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/sfsfseg-2.jpg", "alt": "best pore reduction treatment" },
                { "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/sfsfseg-3.jpg", "alt": "pore reduction treatment" }
            ]
        },
        "product": {
            "id": 318,
            "title": "Pore Reduction Package",
            "price": 699,
            "description": "Comprehensive pore reduction package including consultation, treatment sessions, and follow-up care. Our expert team offers effective pore minimization solutions tailored to your skin."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "أفضل علاج لتقليص المسام في دبي | تصغير المسام الواسعة",
            "description": "علاجات فعالة لتقليص المسام في دبي لتصغير المسام الواسعة وتحسين ملمس البشرة. احصل على بشرة أكثر نعومة وصفاءً مع إجراءاتنا المتخصصة. احجز استشارتك اليوم.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/ar/services/pore-reduction/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "أفضل علاج لتقليص المسام في دبي | تصغير المسام الواسعة",
                "description": "علاجات فعالة لتقليص المسام في دبي لتصغير المسام الواسعة وتحسين ملمس البشرة. احصل على بشرة أكثر نعومة وصفاءً مع إجراءاتنا المتخصصة. احجز استشارتك اليوم.",
                "url": "https://elitebodyhome.com/ar/services/pore-reduction/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "علاج تقليص المسام دبي",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "أفضل علاج لتقليص المسام في دبي | تصغير المسام الواسعة",
                "description": "علاجات فعالة لتقليص المسام في دبي لتصغير المسام الواسعة وتحسين ملمس البشرة. احصل على بشرة أكثر نعومة وصفاءً مع إجراءاتنا المتخصصة. احجز استشارتك اليوم.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "علاج المسام الواسعة"],
        "hero": {
            "title": "علاج المسام الواسعة",
            "subtitle": "",
            "description": "تُعد المسام الواسعة من أكثر المشكلات الشائعة التي تؤثر على مظهر البشرة، حيث تمنحها ملمسًا غير متجانس وترفع من إمكانية تكون الرؤوس السوداء والبيضاء وظهور حب الشباب. لحسن الحظ، توجد طرق علاج فعالة وآمنة تساعد على تقليص حجم المسام وتحسين نسيج البشرة بشكل عام."
        },
        "howItWorks": {
            "title": "إجراء علاج المسام الواسعة",
            "description": "يهدف العلاج إلى تقليل مظهر المسام الواسعة للحصول على بشرة أنعم وأكثر صفاءً."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "الميكرونيدلينغ",
                    "desc": "يحفز إنتاج الكولاجين، مما يساعد على شد البشرة وتقليص حجم المسام تدريجياً بمرور الوقت.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "التقشير الكيميائي",
                    "desc": "يساعد على تقليل مظهر المسام من خلال تقشير طبقات الجلد السطحية وتنظيف المسام من الزيوت الزائدة.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "العلاج بالليزر",
                    "desc": "يستهدف الطبقات العميقة من البشرة، ويحفز إنتاج الكولاجين مما يساهم في تحسين ملمس البشرة وتقليل حجم المسام بشكل واضح.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "البلازما الغنية بالصفائح الدموية",
                    "desc": "يستخدم عوامل النمو المستخلصة من دم المريض لتحفيز تجديد خلايا البشرة وتعزيز إنتاج الكولاجين، مما يؤدي إلى تصغير المسام وتحسين مظهر البشرة بشكل عام.",
                    "icon": "fa-solid fa-dna"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد علاج المسام الواسعة",
            "subheading": "لماذا يعتبر علاج تقليص المسام مفيدًا",
            "para": "لا يقتصر التحسن على تقليص حجم المسام فقط، بل يمتد ليشمل صحة البشرة ومظهرها العام. إليك أبرز الفوائد التي يقدمها العلاج ولماذا يجدر بك التفكير في تجربتها:",
            "items": [
                {
                    "title": "ملمس أكثر نعومة",
                    "desc": "يساعد علاج تصغير المسام على تنعيم سطح البشرة، مما يجعلها تبدو أكثر تساويًا ونقاءً.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "تقليل ظهور حب الشباب والرؤوس السوداء",
                    "desc": "يساهم العلاج في تقليص حجم المسام، مما يقلل من فرص انسدادها وتكون الشوائب التي تؤدي إلى ظهور البثور.",
                    "icon": "fa-solid fa-bacteria"
                },
                {
                    "title": "بشرة أكثر شبابًا",
                    "desc": "تصبح المسام أكثر وضوحًا مع التقدم في العمر، ويساعد تقليصها على استعادة إشراقة البشرة ونضارتها ويجعلها تبدو أصغر سنًا.",
                    "icon": "fa-solid fa-child"
                },
                {
                    "title": "تعزيز الثقة بالنفس",
                    "desc": "تمنحك البشرة الصافية والناعمة شعورًا بالرضا ويعزز الثقة بالنفس في الحياة اليومية.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "يُعد علاج تقليص المسام فعال بشكل عام لمعظم الأشخاص الذين يعانون من المسام الواسعة، لكن يعتمد اختيار العلاج الأنسب على نوع البشرة والحالة الجلدية. فيما يلي الفئات التي تُعد مرشحة مثالية لهذا العلاج:",
            "items": [
                {
                    "title": "أصحاب البشرة الدهنية",
                    "desc": "تجعل الإفرازات الزائدة من الزيوت الطبيعية المسام تبدو أكبر حجمًا، لذا تُعد هذه الطرق مثالية لمن يعانون من البشرة الدهنية.",
                    "icon": "fa-solid fa-oil-can"
                },
                {
                    "title": "البشرة المتقدمة في العمر",
                    "desc": "ينخفض إنتاج الكولاجين مع التقدم في العمر، ما يؤدي إلى ترهل الجلد وظهور المسام بشكل أوضح. لذلك، تعمل تقنيات العلاج المعززة لإنتاج الكولاجين بشكل فعال مع البشرة المتقدمة في العمر.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "البشرة المعرضة لحب الشباب",
                    "desc": "يستفيد الأشخاص الذين يعانون من حب الشباب أو انسداد المسام من العلاج الذي يقلل من حجم المسام ويساعد على تنظيفها بعمق.",
                    "icon": "fa-solid fa-bacteria"
                },
                {
                    "title": "المتضررون من أشعة الشمس",
                    "desc": "تؤدي أشعة الشمس إلى تمدد المسام وفقدان مرونة الجلد. لذا، تتوفر تقنيات علاج مثل الليزر الذي يعالج كلاً من أضرار الشمس وتوسع المسام في آنٍ واحد.",
                    "icon": "fa-solid fa-sun"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد لعلاج المسام الواسعة",
            "subheading": "",
            "para": "يجب الالتزام ببعض التعليمات قبل الخضوع لعلاج المسام الواسعة، لضمان أفضل النتائج وتقليل احتمالية أي آثار جانبية. إليك كيفية الاستعداد:",
            "items": [
                {
                    "title": "تجنب التعرض للشمس",
                    "desc": "قد تؤثر حروق الشمس أو جلسات التان سلبًا على فاعلية العلاج، خاصة في علاج الليزر. ينصح بتجنب التعرض المباشر للشمس واستخدام واقي شمسي بمعامل حماية عالي لمدة لا تقل عن 3 أسابيع قبل الجلسة.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "التوقف عن استخدام المنتجات القاسية على البشرة",
                    "desc": "قد تسبب المنتجات التي تحتوي على الريتينويدات، أو الأحماض أو المقشرات القوية تهيج البشرة أو تقشيرًا. قد يُطلب منك التوقف عن استخدامها قبل الجلسة بعدة أيام حسب توصية طبيب الجلدية.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "تستجيب البشرة المرطبة للعلاج بشكل أفضل وتتعافى بسرعة أكبر. اشرب كميات وفيرة من الماء في الأيام التي تسبق موعد العلاج.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "استشارة طبيب الجلدية",
                    "desc": "قم بحجز استشارة مسبقة لمناقشة أي حالات جلدية أو حساسية قد تؤثر على العلاج. سيساعدك الطبيب على اختيار التقنية المناسبة حسب نوع بشرتك ونتائجك المرجوة.",
                    "icon": "fa-solid fa-user-doctor"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "ما النتائج المتوقعة بعد الإجراء؟",
            "items": [
                {
                    "title": "ملمس بشرة أكثر نعومة وتجانسًا",
                    "desc": "يساعد العلاج في تصغير حجم المسام وتحسين ملمس البشرة بشكل عام.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "تقليل الإفرازات الدهنية وظهور الحبوب",
                    "desc": "عند تنظيف المسام وتقليص حجمها، تقل احتمالية انسدادها وبالتالي تقل فرص ظهور البثور والرؤوس السوداء، خاصة في البشرة الدهنية.",
                    "icon": "fa-solid fa-bacteria"
                },
                {
                    "title": "بشرة أكثر نضارة وشبابًا",
                    "desc": "لا تكتفي تقنيات العلاج بتحسين ملمس البشرة فقط، بل تعزز من إشراقتها ومظهرها الشبابي.",
                    "icon": "fa-solid fa-face-smile"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة المكررة",
            "subheading": "",
            "items": [
                {
                    "question": "كم من الوقت يستغرق ظهور نتائج علاج المسام الواسعة؟",
                    "answer": "تختلف النتائج حسب نوع العلاج. تظهر تحسنات في الأسابيع القليلة الأولى بعد العلاج بالليزر والميكرونيدلينغ، بينما يوفر التقشير الكيميائي نتائج أكثر سرعة. تظهر الفوائد الكاملة بعد مجموعة من الجلسات عادةً."
                },
                {
                    "question": "هل علاج تصغير المسام مؤلم؟",
                    "answer": "تُعد معظم تقنيات العلاج المتوفرة غير جراحية وتسبب إزعاجًا بسيطًا أو لا تسبب أي إزعاج. قد يشعر البعض بوخز خفيف أو تنميل أثناء جلسات الليزر أو الميكرونيدلينغ، ولكن غالبًا ما تُستخدم كريمات تخدير موضعية لتقليل الألم."
                },
                {
                    "question": "هل يمكن دمج علاج المسام الواسعة مع إجراءات أخرى للعناية بالبشرة؟",
                    "answer": "نعم، يمكن دمجه مع إجراءات تجميلية أخرى مثل الفيشال، أو الديرما بلانينج، أو حتى مع إجراءات مكافحة الشيخوخة مثل البوتوكس أو الفيلر لتحسين ملمس البشرة ومظهرها بشكل عام."
                },
                {
                    "question": "هل يتطلب فترة تعافي بعد العلاج؟",
                    "answer": "تتطلب معظم أنواع العلاج فترة تعافي قصيرة جداً. قد يحتاج العلاج بالليزر والتقشير الكيميائي العميق إلى بضعة أيام للراحة، بينما تسمح بعض أنواع العلاج مثل الهيدرافيشل والميكرونيدلينغ بالعودة إلى الأنشطة اليومية فورًا تقريبًا."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }
}
